import { useState, useEffect } from 'react'
import { Plus, Search, Edit, Trash2, X, AlertCircle, Users, UserPlus, UserMinus } from 'lucide-react'
import { customersApi, usersApi, expenseApi, getErrorMessage } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'

export default function Customers() {
  const { user: currentUser } = useAuth()
  const isAdmin = currentUser?.role === 'admin'
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [formData, setFormData] = useState({
    customer_name: '',
    company: '',
    name: '',
    phone: '',
    email: '',
    azure_subscription_id: '',
    status: 'active',
    no_prepay: false
  })
  const [error, setError] = useState('')

  // 編輯者管理
  const [showEditorModal, setShowEditorModal] = useState(false)
  const [editorCustomer, setEditorCustomer] = useState(null)
  const [editors, setEditors] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [editorLoading, setEditorLoading] = useState(false)
  const [editorError, setEditorError] = useState('')

  useEffect(() => {
    loadCustomers()
  }, [])

  const loadCustomers = async () => {
    try {
      const response = await customersApi.getAll()
      setCustomers(response.data.data)
    } catch (error) {
      console.error('載入客戶失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  const openModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer)
      setFormData({
        customer_name: customer.customer_name || '',
        company: customer.company || '',
        name: customer.name,
        phone: customer.phone || '',
        email: customer.email || '',
        azure_subscription_id: customer.azure_subscription_id || '',
        status: customer.status,
        no_prepay: customer.no_prepay || false
      })
    } else {
      setEditingCustomer(null)
      setFormData({
        customer_name: '',
        company: '',
        name: '',
        phone: '',
        email: '',
        azure_subscription_id: '',
        status: 'active',
        no_prepay: false
      })
    }
    setError('')
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingCustomer(null)
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    try {
      if (editingCustomer) {
        await customersApi.update(editingCustomer.id, formData)
      } else {
        await customersApi.create(formData)
      }
      await loadCustomers()
      closeModal()
    } catch (error) {
      setError(getErrorMessage(error, '操作失敗'))
    }
  }

  const handleDelete = async (customer) => {
    try {
      // 先檢查客戶是否有相關數據
      let hasReports = false
      let hasBudgets = false
      let reportsCount = 0
      let budgetsCount = 0
      
      try {
        const checkResponse = await customersApi.getById(customer.id)
        const customerData = checkResponse.data.data
        reportsCount = customerData.expense_reports_count || 0
        budgetsCount = customerData.budgets_count || 0
        hasReports = reportsCount > 0
        hasBudgets = budgetsCount > 0
      } catch (checkError) {
        console.warn('無法獲取客戶數據統計,將繼續刪除流程', checkError)
      }
      
      let confirmMessage = `確定要刪除客戶「${customer.customer_name}」嗎?`
      
      if (hasReports || hasBudgets) {
        confirmMessage = `⚠️ 此客戶有相關數據:\n\n`
        if (budgetsCount > 0) confirmMessage += `• 預算記錄: ${budgetsCount} 筆 (將自動取消)\n`
        if (reportsCount > 0) confirmMessage += `• 費用報表: ${reportsCount} 筆 (將自動刪除)\n`
        confirmMessage += `\n確定要繼續嗎?`
      }
      
      if (!confirm(confirmMessage)) return

      // 後端會自動處理預算取消和報表刪除
      await customersApi.delete(customer.id)
      await loadCustomers()
      
      if (hasReports || hasBudgets) {
        alert('✅ 客戶及相關數據已刪除!')
      } else {
        alert('✅ 客戶刪除成功!')
      }
    } catch (error) {
      console.error('刪除客戶失敗:', error)
      const errorData = error.response?.data
      const errorMsg = errorData?.error || error.message || '未知錯誤'
      alert('❌ 刪除失敗: ' + errorMsg)
    }
  }

  const handleDeleteWithReports = async (customerId) => {
    try {
      // 先刪除所有報表
      await expenseApi.deleteCustomerReports(customerId)
      
      // 再刪除客戶
      await customersApi.delete(customerId)
      await loadCustomers()
      alert('客戶及相關報表已刪除!')
    } catch (error) {
      alert(getErrorMessage(error, '刪除失敗'))
    }
  }

  // 檢查用戶是否有該客戶的編輯權限
  const canEdit = (customer) => {
    if (isAdmin) return true
    if (customer.created_by_user_id === currentUser?.id) return true
    if (customer.editor_ids?.includes(currentUser?.id)) return true
    return false
  }

  // 檢查是否為建立者或管理員（可管理編輯者權限）
  const canManageEditors = (customer) => {
    if (isAdmin) return true
    if (customer.created_by_user_id === currentUser?.id) return true
    return false
  }

  // ======= 編輯者管理 =======
  const openEditorModal = async (customer) => {
    setEditorCustomer(customer)
    setEditorError('')
    setSelectedUserId('')
    setShowEditorModal(true)
    setEditorLoading(true)
    try {
      const [editorsRes, usersRes] = await Promise.all([
        customersApi.getEditors(customer.id),
        usersApi.getAll()
      ])
      setEditors(editorsRes.data.data || [])
      setAllUsers(usersRes.data.users || [])
    } catch (err) {
      setEditorError(getErrorMessage(err, '載入失敗'))
    } finally {
      setEditorLoading(false)
    }
  }

  const handleAddEditor = async () => {
    if (!selectedUserId || !editorCustomer) return
    setEditorError('')
    try {
      await customersApi.addEditor(editorCustomer.id, Number(selectedUserId))
      // 重新載入
      const res = await customersApi.getEditors(editorCustomer.id)
      setEditors(res.data.data || [])
      setSelectedUserId('')
      await loadCustomers()  // 重新載入客戶列表以更新 editor_ids
    } catch (err) {
      setEditorError(getErrorMessage(err, '新增失敗'))
    }
  }

  const handleRemoveEditor = async (userId) => {
    if (!editorCustomer) return
    setEditorError('')
    try {
      await customersApi.removeEditor(editorCustomer.id, userId)
      const res = await customersApi.getEditors(editorCustomer.id)
      setEditors(res.data.data || [])
      await loadCustomers()
    } catch (err) {
      setEditorError(getErrorMessage(err, '移除失敗'))
    }
  }

  const filteredCustomers = customers.filter(customer =>
    (customer.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.company || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
    </div>
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">客戶管理</h1>
        <button 
          onClick={() => openModal()}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>新增客戶</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="搜尋客戶..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">客戶名稱</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">單位</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">姓名</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">電話</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">餘額</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">建立者</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">狀態</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{customer.customer_name || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.company || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.phone || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.email || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${customer.balance.toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.created_by || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <span
                    className={`inline-block w-3 h-3 rounded-full ${
                      customer.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    title={customer.status === 'active' ? '啟用中' : '停用'}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-2">
                    {canEdit(customer) ? (
                      <>
                        <button 
                          onClick={() => openModal(customer)}
                          className="text-blue-600 hover:text-blue-800"
                          title="編輯"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(customer)}
                          className="text-red-600 hover:text-red-800"
                          title="刪除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">無權限</span>
                    )}
                    {canManageEditors(customer) && (
                      <button
                        onClick={() => openEditorModal(customer)}
                        className="text-purple-600 hover:text-purple-800"
                        title="管理編輯權限"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 新增/編輯客戶 Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCustomer ? '編輯客戶' : '新增客戶'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  客戶名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  單位 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  電話
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Azure 訂閱 ID
                </label>
                <input
                  type="text"
                  value={formData.azure_subscription_id}
                  onChange={(e) => setFormData({...formData, azure_subscription_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  狀態
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formData.no_prepay}
                    onChange={(e) => setFormData({...formData, no_prepay: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">無預收</span>
                  <span className="text-xs text-gray-400">(每月另開發票請款，無預收款)</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  {editingCustomer ? '更新' : '新增'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 編輯者管理 Modal */}
      {showEditorModal && editorCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-600" />
                <span>編輯權限管理</span>
              </h2>
              <button onClick={() => setShowEditorModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-3 text-sm text-gray-600">
              客戶：<span className="font-medium text-gray-900">{editorCustomer.customer_name}</span>
              <span className="text-gray-400 ml-2">（{editorCustomer.company}）</span>
            </div>

            {editorError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {editorError}
              </div>
            )}

            {editorLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <>
                {/* 新增編輯者 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">新增編輯者</label>
                  <div className="flex space-x-2">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    >
                      <option value="">選擇用戶...</option>
                      {allUsers
                        .filter(u => 
                          u.id !== editorCustomer.created_by_user_id &&  // 排除建立者
                          !editors.some(e => e.user_id === u.id) &&      // 排除已有權限的
                          u.is_active                                     // 只顯示啟用的
                        )
                        .map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))
                      }
                    </select>
                    <button
                      onClick={handleAddEditor}
                      disabled={!selectedUserId}
                      className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 現有編輯者列表 */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">已授權的編輯者</h3>
                  {editors.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">尚未授權其他用戶</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {editors.map(editor => (
                        <div key={editor.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {editor.user_avatar ? (
                              <img src={editor.user_avatar} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-purple-600 text-sm font-medium">
                                  {(editor.user_name || '?')[0]}
                                </span>
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">{editor.user_name}</div>
                              <div className="text-xs text-gray-500">{editor.user_email}</div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveEditor(editor.user_id)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="移除權限"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 建立者資訊 */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-400">
                    建立者：{editorCustomer.created_by || '未知'}（自動擁有權限）
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
